import { assets, playSound } from './assets';
import type { Position } from './backend';
import type { Renderer } from './game';

export abstract class Particle {
  done = false;
  constructor(public atTimeMs: number) {}

  public abstract draw(
    context: CanvasRenderingContext2D,
    tMs: number,
    renderer: Renderer
  );
}

export class HealthChangeParticle extends Particle {
  offset: Position = { x: 0, y: 0 };

  constructor(
    public atTimeMs: number,
    public damage: number | string,
    public loc: { x: number; y: number },
    public color: string
  ) {
    super(atTimeMs);
  }

  public draw(
    context: CanvasRenderingContext2D,
    tMs: number,
    renderer: Renderer
  ) {
    this.offset.y -= 1;
    this.offset.x += Math.random() * 2 - 1;

    if (this.offset.y < -20) {
      this.done = true;
    }

    const { x: canvasX, y: canvasY } = renderer.toCanvasCoords(
      this.loc.x,
      this.loc.y
    );

    // write damage number at loc + offset
    context.fillStyle = this.color;
    context.font = 'bold 16px sans-serif';
    context.fillText(
      this.damage.toString(),
      canvasX + this.offset.x,
      canvasY + this.offset.y
    );
  }
}

export class BulletParticle extends Particle {
  BULLET_SPEED = 40;
  constructor(
    public atTimeMs: number,
    public from: { x: number; y: number },
    public to: { x: number; y: number },
    public hit: boolean
  ) {
    super(atTimeMs);

    playSound(assets.sounds.pistol);
  }

  public draw(
    context: CanvasRenderingContext2D,
    tMs: number,
    renderer: Renderer
  ) {
    const t2 = (tMs - this.atTimeMs) / 1000;
    const distTravelled = t2 * this.BULLET_SPEED;
    const totalDist = Math.hypot(
      this.to.x - this.from.x,
      this.to.y - this.from.y
    );

    if (distTravelled > totalDist) {
      this.done = true;
      return;
    }

    const x =
      this.from.x + (this.to.x - this.from.x) * (distTravelled / totalDist);
    const y =
      this.from.y + (this.to.y - this.from.y) * (distTravelled / totalDist);

    const { x: canvasX, y: canvasY } = renderer.toCanvasCoords(x, y);

    context.fillStyle = 'black';
    context.beginPath();
    context.arc(canvasX, canvasY, 2, 0, 2 * Math.PI);
    context.fill();
  }
}

export class ExplosionParticle extends Particle {
  constructor(public atTimeMs: number, public tiles: Array<[number, number]>) {
    super(atTimeMs);
    playSound(assets.sounds.grenade);
  }

  public draw(
    context: CanvasRenderingContext2D,
    tMs: number,
    renderer: Renderer
  ) {
    const t2 = tMs - this.atTimeMs;
    if (t2 > 1000) {
      this.done = true;
      return;
    }

    for (const [x, y] of this.tiles) {
      const { x: canvasX, y: canvasY } = renderer.toCanvasCoords(x, y);

      const color = `rgba(255, ${Math.floor(Math.random() * 255)}, 0, ${
        (1 - t2 / 1000) * 0.5
      })`;

      const radius = Math.random() * 8 + 5;
      const displacementX = Math.random() * radius - radius / 2;
      const displacementY = Math.random() * radius - radius / 2;

      context.fillStyle = color;
      context.beginPath();
      context.arc(
        canvasX + displacementX + renderer.tileSize / 2,
        canvasY + displacementY + renderer.tileSize / 2,
        radius,
        0,
        2 * Math.PI
      );
      context.fill();
    }
  }
}
